import { useState, useRef, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';

// ─── Tab Definitions ───────────────────────────────────────────
export interface TabDef {
    id: string;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
}

// Keep TabGroup export for backwards compat but it's unused now
export interface TabGroup {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    tabs: TabDef[];
}

interface NavDropdownProps {
    groups: TabGroup[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
}

// ─── Flat Dropdown Navigation (inside Header) ─────────────────
export function NavDropdown({ groups, activeTab, onTabChange }: NavDropdownProps) {
    const [open, setOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Flatten all tabs in order
    const allTabs = groups.flatMap(g => g.tabs);
    const activeTabDef = allTabs.find(t => t.id === activeTab) || allTabs[0];

    // Close on outside click
    useEffect(() => {
        if (!open) return;
        const handleClick = (e: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    // Close on escape
    useEffect(() => {
        if (!open) return;
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open]);

    return (
        <div ref={dropdownRef} className="relative">
            {/* Trigger — compact pill */}
            <button
                onClick={() => setOpen(!open)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg transition-all duration-200 hover:bg-white/5"
            >
                {activeTabDef && (
                    <activeTabDef.icon
                        className="size-3.5 text-amber-400 drop-shadow-[0_0_6px_rgba(245,158,11,0.5)]"
                    />
                )}
                <span
                    className="text-amber-400 font-semibold text-xs tracking-wide uppercase"
                    style={{ textShadow: '0 0 10px rgba(245,158,11,0.3)' }}
                >
                    {activeTabDef?.label}
                </span>
                <ChevronDown
                    className={`size-3 text-amber-400/50 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
                />
            </button>

            {/* Dropdown Panel — flat list */}
            {open && (
                <div
                    className="absolute top-full right-0 mt-2 z-[60] w-[180px] rounded-xl overflow-hidden"
                    style={{
                        background: 'linear-gradient(180deg, rgba(12,12,30,0.99) 0%, rgba(8,8,22,0.99) 100%)',
                        backdropFilter: 'blur(24px)',
                        animation: 'navDropdownSlideIn 150ms ease-out',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.7), 0 0 1px rgba(139,92,246,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
                        border: '1px solid rgba(139,92,246,0.15)',
                    }}
                >
                    <div className="py-1.5">
                        {allTabs.map(tab => {
                            const isActive = activeTab === tab.id;
                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => {
                                        onTabChange(tab.id);
                                        setOpen(false);
                                    }}
                                    className={`w-full flex items-center gap-2.5 px-4 py-2 text-left transition-all duration-100 ${isActive
                                            ? 'bg-amber-500/10'
                                            : 'hover:bg-white/[0.04]'
                                        }`}
                                >
                                    <tab.icon
                                        className={`size-3.5 flex-shrink-0 transition-colors ${isActive
                                                ? 'text-amber-400 drop-shadow-[0_0_4px_rgba(245,158,11,0.5)]'
                                                : 'text-white/25'
                                            }`}
                                    />
                                    <span
                                        className={`text-[11px] font-semibold tracking-wide uppercase flex-1 ${isActive ? 'text-amber-400' : 'text-white/45'
                                            }`}
                                        style={isActive ? { textShadow: '0 0 10px rgba(245,158,11,0.25)' } : {}}
                                    >
                                        {tab.label}
                                    </span>
                                    {isActive && (
                                        <span
                                            className="w-1 h-1 rounded-full bg-amber-400 animate-pulse flex-shrink-0"
                                            style={{ boxShadow: '0 0 4px rgba(245,158,11,0.8)' }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Keyframes */}
            <style>{`
        @keyframes navDropdownSlideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
        </div>
    );
}
